import { Link } from 'react-router-dom';

export default function TermsPage() {
  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="legal-hero">
        <div className="container">
          <div style={{ maxWidth: 680 }}>
            <div className="section-label">Legal</div>
            <h1 className="text-headline" style={{ marginBottom: 12 }}>Terms & Conditions</h1>
            <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-muted)' }}>
              Effective date: <strong style={{ color: 'var(--text-secondary)' }}>June 2026</strong>
            </p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 8 }}>
              Last updated: June 2026 · Applies to: Klar mobile application (iOS &amp; Android) and the klar website
            </p>
          </div>
        </div>
      </section>

      <div className="legal-content">

        {/* Intro highlight */}
        <div className="legal-highlight">
          <p>
            <strong>Plain English summary:</strong> Klar is a free personal finance tracker for Indian college students. By using it, you agree not to misuse it. Your data stays on your device. We make no warranties that Klar is error-free. We're not liable for financial decisions you make based on Klar's data.
          </p>
        </div>

        {/* Table of contents */}
        <div className="legal-toc">
          <div className="legal-toc-title">Contents</div>
          <ol className="legal-toc-list" style={{ listStyle: 'decimal', paddingLeft: 20, gap: 8 }}>
            {[
              'Acceptance of Terms',
              'Eligibility',
              'Use of Service',
              'User Accounts and Data',
              'Prohibited Uses',
              'Intellectual Property',
              'Disclaimer of Warranties',
              'Limitation of Liability',
              'Indemnification',
              'Changes to Terms',
              'Governing Law',
              'Contact',
            ].map(item => (
              <li key={item} style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', paddingLeft: 4 }}>{item}</li>
            ))}
          </ol>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">1. Acceptance of Terms</h2>
          <div className="legal-body">
            <p>
              By downloading, installing, or using the Klar mobile application ("the App") or visiting the Klar website ("the Site"), you agree to be bound by these Terms & Conditions ("Terms"). If you do not agree to these Terms, do not use the App or Site.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and Equios ("we", "us", or "our"), the developer and operator of Klar. By accessing or using Klar, you represent that you have read, understood, and agree to be bound by these Terms.
            </p>
          </div>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">2. Eligibility</h2>
          <div className="legal-body">
            <p>
              Klar is designed for Indian college students and individuals managing personal finances. To use Klar, you must:
            </p>
            <ul>
              <li>Be at least 16 years of age</li>
              <li>Have a valid Google account to complete sign-in</li>
              <li>Use the App only for personal, non-commercial budgeting purposes</li>
              <li>Not be prohibited from using the App under applicable Indian law</li>
            </ul>
            <p>
              By using Klar, you represent and warrant that you meet all eligibility requirements listed above.
            </p>
          </div>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">3. Use of Service</h2>
          <div className="legal-body">
            <p>
              Klar grants you a limited, non-exclusive, non-transferable, revocable licence to use the App for personal budgeting purposes, subject to these Terms.
            </p>
            <p>
              <strong>Availability:</strong> Klar is publicly available and free to download on the Apple App Store and Google Play. We reserve the right to modify, suspend, or discontinue the App (or any part of it) at any time without notice.
            </p>
            <p>
              <strong>Free service:</strong> Klar is provided free of charge. There is no subscription, no in-app purchase, and no premium tier. We reserve the right to introduce optional paid features in the future, but will not remove existing free functionality without adequate notice to users.
            </p>
            <p>
              <strong>Updates:</strong> We may release updates to the App from time to time. We recommend keeping the App updated to ensure access to the latest features and security improvements.
            </p>
          </div>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">4. User Accounts and Data</h2>
          <div className="legal-body">
            <p>
              You are responsible for maintaining the security of the device on which Klar is installed. Since all data is stored locally on your device, loss of your device or uninstallation of the App will result in permanent loss of your expense data.
            </p>
            <p>
              <strong>Accuracy of data:</strong> All expense and budget data you enter into Klar is entered manually by you. We make no representations about the accuracy, completeness, or reliability of data you choose to input. Klar is a tool to assist your financial awareness — it is not a substitute for professional financial advice.
            </p>
            <p>
              <strong>Account termination:</strong> We reserve the right to suspend or terminate your access to Klar if you violate these Terms or engage in conduct that we determine, at our sole discretion, to be harmful to other users, the App, or Equios.
            </p>
          </div>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">5. Prohibited Uses</h2>
          <div className="legal-body">
            <p>You agree not to use Klar for any of the following:</p>
            <ul>
              <li>Any unlawful purpose or in violation of applicable Indian laws and regulations</li>
              <li>Reverse engineering, decompiling, or attempting to extract the source code of the App</li>
              <li>Modifying, adapting, translating, or creating derivative works based on the App</li>
              <li>Attempting to gain unauthorised access to any part of the App or related systems</li>
              <li>Distributing, selling, sublicensing, or otherwise commercially exploiting the App</li>
              <li>Using the App in any manner that could damage, disable, or impair Klar's infrastructure</li>
              <li>Impersonating any person or entity, or falsely stating your affiliation with any person or entity</li>
              <li>Uploading or transmitting malicious code, viruses, or any harmful data</li>
              <li>Using the App to process financial data on behalf of other individuals without their explicit consent</li>
            </ul>
          </div>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">6. Intellectual Property</h2>
          <div className="legal-body">
            <p>
              The Klar App, including but not limited to its design, graphics, user interface, source code, and all content within the App (excluding data you input), is the exclusive intellectual property of Equios and is protected by applicable copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              The name "Klar", the "K" logo mark, and associated branding are proprietary to Equios. You may not use these marks without prior written permission.
            </p>
            <p>
              Nothing in these Terms transfers any intellectual property rights to you. Your use of Klar grants you only the limited licence described in Section 3.
            </p>
          </div>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">7. Disclaimer of Warranties</h2>
          <div className="legal-body">
            <p>
              <strong>Klar is provided "as is" and "as available" without warranty of any kind.</strong> To the fullest extent permitted by applicable law, Equios expressly disclaims all warranties, whether express, implied, statutory, or otherwise, including but not limited to:
            </p>
            <ul>
              <li>Implied warranties of merchantability and fitness for a particular purpose</li>
              <li>Warranties that Klar will be error-free, uninterrupted, or secure</li>
              <li>Warranties that the App will meet your specific requirements</li>
              <li>Warranties regarding the accuracy of any financial calculations or insights generated by the App</li>
            </ul>
            <p>
              <strong>Financial decisions:</strong> Klar is a personal budgeting tool, not a financial advisory service. Any financial decisions you make based on data or insights from Klar are made solely at your own risk. We strongly recommend consulting a qualified financial advisor for significant financial decisions.
            </p>
          </div>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">8. Limitation of Liability</h2>
          <div className="legal-body">
            <p>
              To the maximum extent permitted by applicable law, Equios and its founders, employees, contractors, and affiliates shall not be liable for:
            </p>
            <ul>
              <li>Any indirect, incidental, special, consequential, or punitive damages arising from your use of Klar</li>
              <li>Loss of data resulting from uninstallation, device failure, or data corruption</li>
              <li>Financial losses resulting from decisions made based on information displayed in Klar</li>
              <li>Any interruption, suspension, or termination of the App or any features within it</li>
              <li>Unauthorised access to your device or the data stored locally on your device</li>
            </ul>
            <p>
              In jurisdictions that do not allow the exclusion or limitation of liability for consequential or incidental damages, Equios's liability is limited to the maximum extent permitted by law. Since Klar is a free service, any liability is further limited to the amount you paid for the App, which is ₹0.
            </p>
          </div>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">9. Indemnification</h2>
          <div className="legal-body">
            <p>
              You agree to indemnify, defend, and hold harmless Equios and its founders, employees, and contractors from any claims, damages, losses, liabilities, costs, or expenses (including reasonable legal fees) arising from:
            </p>
            <ul>
              <li>Your violation of these Terms</li>
              <li>Your use of Klar in a manner not authorised by these Terms</li>
              <li>Your violation of any applicable law or regulation</li>
              <li>Your infringement of any third-party intellectual property or other rights</li>
            </ul>
          </div>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">10. Changes to Terms</h2>
          <div className="legal-body">
            <p>
              We reserve the right to modify these Terms at any time. When we make material changes, we will:
            </p>
            <ul>
              <li>Update the "Effective date" and "Last updated" dates at the top of this page</li>
              <li>Notify users in-app or via email where appropriate before the changes take effect</li>
            </ul>
            <p>
              Your continued use of Klar after any changes to the Terms constitutes your acceptance of the revised Terms. If you do not agree to the revised Terms, you must stop using Klar.
            </p>
          </div>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">11. Governing Law</h2>
          <div className="legal-body">
            <p>
              These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the competent courts in India.
            </p>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid under applicable law, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
            </p>
          </div>
        </div>

        <div className="legal-section">
          <h2 className="legal-section-title">12. Contact</h2>
          <div className="legal-body">
            <p>
              If you have any questions about these Terms, or wish to report a violation, please contact us:
            </p>
            <ul>
              <li><strong>Email:</strong> <a href="mailto:affan@equios.co">affan@equios.co</a></li>
              <li><strong>Subject line:</strong> "Klar Terms — [your question]"</li>
            </ul>
            <p>We aim to respond to all correspondence within 3–5 business days.</p>
          </div>
        </div>

        {/* Navigation links */}
        <div style={{ display: 'flex', gap: 16, paddingTop: 32, borderTop: '1px solid var(--border)' }}>
          <Link to="/privacy" className="btn btn-ghost btn-sm">Privacy Policy →</Link>
          <Link to="/contact" className="btn btn-ghost btn-sm">Contact Us →</Link>
        </div>
      </div>
    </>
  );
}
